import type { ArtifactSourceState, ArtifactThemeMode, ErrorMessage } from "@entrilia/shared/artifactRuntime/protocol";
import type { SmartViewArtifactVersion } from "../../../../../api/types/smartViewArtifactsTypes";
import type { SourceNote } from "./sourceRequestsState";
import { toSourceNote } from "./sourceRequestsState";

export const CHECK_STEP_TIMEOUT_MS = 120_000;

export interface ArtifactPageProblem {
  message: string;
  view: string;
  viewTitle: string;
  mode: ArtifactThemeMode;
}

export interface ArtifactRuntimeError extends ArtifactPageProblem {
  stack?: string;
}

export interface ArtifactSourceNote extends SourceNote {
  sourceTitle: string;
  view: string;
  viewTitle: string;
}

export interface ArtifactCheckProblems {
  errors: ArtifactRuntimeError[];
  problems: ArtifactPageProblem[];
  warnings: string[];
}

export type ArtifactCheckStop =
  { reason: "notStarted" } | { reason: "timedOut"; view: string; viewTitle: string; mode: ArtifactThemeMode };

export type ArtifactCompareSkip = "hidden" | "failed";

export interface ArtifactCheckResult extends ArtifactCheckProblems {
  version: number;
  sourceNotes: ArtifactSourceNote[];
  stop?: ArtifactCheckStop;
  compareRender?: Blob;
  compareSkip?: ArtifactCompareSkip;
}

export const needsFix = ({ errors, problems }: ArtifactCheckProblems): boolean =>
  errors.length > 0 || problems.length > 0;

export const findViewTitle = (version: SmartViewArtifactVersion, view: string): string =>
  version.views.find(({ key }) => key === view)?.title ?? "";

export const findSourceTitle = (version: SmartViewArtifactVersion, sourceKey: string): string =>
  version.sources.find(({ key }) => key === sourceKey)?.title ?? "";

export const toRuntimeError = (
  version: SmartViewArtifactVersion,
  { message, stack, view, mode }: ErrorMessage
): ArtifactRuntimeError => ({
  message,
  stack,
  view,
  viewTitle: findViewTitle(version, view),
  mode,
});

export const findSourceNote = (
  version: SmartViewArtifactVersion,
  sourceKey: string,
  state: ArtifactSourceState,
  view: string
): ArtifactSourceNote | undefined => {
  const note = toSourceNote(sourceKey, state);
  return (
    note && { ...note, sourceTitle: findSourceTitle(version, sourceKey), view, viewTitle: findViewTitle(version, view) }
  );
};
